import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

import type { TransactionClaimsOut } from '@/api/claimedLines';
import type {
  InvoiceExtractionOut,
  InvoiceLinkOut,
} from '@/api/invoices';
import { getRuleVersion } from '@/api/rules';
import type {
  FindingOut,
  RuleOut,
} from '@/api/types';
import { FindingCard } from '@/components/FindingCard';
import { FindingFilterBar } from '@/components/FindingFilterBar';
import { ReasonCodesPicker } from '@/components/ReasonCodesPicker';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  type FindingsReview,
} from '@/hooks/useFindingsReview';
import { scanForPending } from '@/lib/findingsPager';
import { formatCurrency } from '@/lib/format';
import { submitFormOnCmdEnter } from '@/lib/formKeys';
import { cn } from '@/lib/utils';
import { useAuth } from '../auth/AuthContext';

// ── Review queue ─────────────────────────────────────────────────────
//
// One-card-at-a-time review flow. Replaces the prior findings table.
// The practitioner scans summary stats at the top (progress bar +
// approved/rejected/pending chips), then makes a single decision on the
// current card and advances. Findings are ordered pending-first then by
// recovery desc — the next card is always the next-most-valuable piece
// of work. Filter narrows the queue to a single rule; nav stays
// keyboard-friendly (← / → between cards).

/** Un-dispositioned = still needs a decision: `pending` or the
 *  explicitly-flagged `needs_review`. Drives the n / p skip keys. */
const isUnDispositioned = (s: string) => s === 'pending' || s === 'needs_review';

/** Muted marker for a transaction triaged "no invoice available" (INP-494) —
 *  distinct from simply un-attached (not yet collected). */
export function FindingsReviewQueue({
  review,
  findings,
  rulesLoading,
  rulesError,
  canReview,
  runId,
  linksByTransaction,
  extractionsByDocument,
  markedTransactions,
  linksReady,
  linksError,
  claimsByTransaction,
  claimPeriodCount,
  claimsReady,
}: {
  review: FindingsReview;
  findings: FindingOut[];
  rulesLoading: boolean;
  rulesError: boolean;
  canReview: boolean;
  runId: string;
  linksByTransaction: Map<string, InvoiceLinkOut[]>;
  extractionsByDocument: Map<string, InvoiceExtractionOut>;
  markedTransactions: Set<string>;
  linksReady: boolean;
  linksError: boolean;
  claimsByTransaction: Map<string, TransactionClaimsOut>;
  claimPeriodCount: number;
  claimsReady: boolean;
}) {
  const { userKey, getIdToken } = useAuth();

  const {
    ruleIndex,
    ruleById,
    ruleFilter,
    setRuleFilter,
    provinceFilter,
    setProvinceFilter,
    supplierFilter,
    setSupplierFilter,
    transactionTypeFilter,
    setTransactionTypeFilter,
    classFilter,
    setClassFilter,
    glAccountFilter,
    setGlAccountFilter,
    currencyFilter,
    setCurrencyFilter,
    outcomeFilter,
    setOutcomeFilter,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    approvalReasonFilter,
    setApprovalReasonFilter,
    reasonCodesFilter,
    setReasonCodesFilter,
    amountMin,
    setAmountMin,
    amountMax,
    setAmountMax,
    effRateMin,
    setEffRateMin,
    effRateMax,
    setEffRateMax,
    carriedOnlyFilter,
    setCarriedOnlyFilter,
    invoiceFilter,
    setInvoiceFilter,
    sourceFilter,
    setSourceFilter,
    sources,
    ledgerSourceFilter,
    setLedgerSourceFilter,
    ledgerSources,
    ruleIds,
    provinces,
    suppliers,
    transactionTypes,
    classes,
    currencies,
    pendingBySupplier,
    filtered,
    filterKey,
    queueItems,
    queueTotal,
    ensureQueueIndex,
    queueError,
    retryQueue,
    hasActiveFilter,
    filteredTotal,
    attachedCount,
    reviewedCount,
    totalCount,
    reviewedPct,
    bulkApproveOpen,
    setBulkApproveOpen,
    bulkApproveNote,
    setBulkApproveNote,
    bulkRejectOpen,
    setBulkRejectOpen,
    bulkRejectReason,
    setBulkRejectReason,
    bulkRejectCodes,
    setBulkRejectCodes,
    bulkNeedsReviewOpen,
    setBulkNeedsReviewOpen,
    bulkNeedsReviewNote,
    setBulkNeedsReviewNote,
    bulkProgress,
    runBulkApprove,
    runBulkNeedsReview,
    runBulkReject,
    clearAllFilters,
    startBulkForSupplier,
  } = review;

  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const openSupplierPage = (name: string, supplierId: string | null) => {
    // Route by the stable supplier id when we have one — a name with a `/`
    // (e.g. "Microsoft Office / Azure") can't survive a URL path segment.
    if (supplierId) {
      void navigate({
        to: '/runs/$id/suppliers/by-id/$supplierId',
        params: { id: runId, supplierId },
      });
      return;
    }
    void navigate({
      to: '/runs/$id/suppliers/$supplierName',
      params: { id: runId, supplierName: name },
    });
  };

  // Filters are commit-and-restart — practitioner wants to see the top
  // of the new narrowed queue, not get dropped at the previous tail.
  useEffect(() => {
    setCurrentIndex(0);
  }, [filterKey]);

  useEffect(() => {
    if (currentIndex >= queueItems.length) {
      setCurrentIndex(Math.max(0, queueItems.length - 1));
    }
  }, [queueItems.length, currentIndex]);

  // Server-cursor prefetch: keep a runway of loaded cards ahead of the
  // cursor so advancing never waits on the network (no-op in
  // client-filtered mode, where everything is already in memory).
  useEffect(() => {
    ensureQueueIndex(currentIndex);
  }, [ensureQueueIndex, currentIndex]);

  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () =>
    setCurrentIndex((i) => Math.min(queueItems.length - 1, i + 1));
  // Skip to the next/prev finding whose status is still un-dispositioned
  // (`pending` or `needs_review`) so a long review session can fly past
  // the rows the practitioner has already dispositioned (carried-forward
  // or just-reviewed) — but still revisit anything flagged for follow-up.
  //
  // Forward (`n`) must keep working in paged mode when every LOADED card
  // after the cursor is dispositioned but pending ones remain unloaded on
  // the server: `pendingSeekFrom` records the scan origin, and the effect
  // below fetches page after page (via ensureQueueIndex) and rescans as
  // each lands, until a pending card is found or the queue is exhausted.
  // Backward never needs the walk — everything before the cursor is
  // loaded by construction.
  const [pendingSeekFrom, setPendingSeekFrom] = useState<number | null>(null);
  useEffect(() => {
    setPendingSeekFrom(null);
  }, [filterKey]);
  useEffect(() => {
    if (pendingSeekFrom === null) return;
    const scan = scanForPending(
      queueItems.map((f) => f.status),
      pendingSeekFrom,
      queueTotal,
      isUnDispositioned,
    );
    if (scan.kind === 'found') {
      setCurrentIndex(scan.index);
      setPendingSeekFrom(null);
    } else if (scan.kind === 'none') {
      setPendingSeekFrom(null);
    } else {
      // Ask the pager for the next page; this effect rescans when the
      // loaded window grows. Terminates naturally: once the server is
      // exhausted the queue stops growing and the scan resolves to
      // found/none on its final pass.
      ensureQueueIndex(queueItems.length);
    }
  }, [pendingSeekFrom, queueItems, queueTotal, ensureQueueIndex]);
  const goNextPending = () => setPendingSeekFrom(currentIndex + 1);
  const goPrevPending = () => {
    setCurrentIndex((i) => {
      for (let j = i - 1; j >= 0; j--) {
        if (isUnDispositioned(queueItems[j].status)) return j;
      }
      return i;
    });
  };

  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  // Page-level keyboard shortcuts. Anything that conflicts with text
  // input (j/k/n/p/a/r/e/?) skips when a textarea / input / select
  // currently owns focus.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      const inTextField = tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT';

      // Escape always works (closes shortcuts dialog + blurs focused field).
      if (e.key === 'Escape') {
        if (shortcutsOpen) {
          setShortcutsOpen(false);
          e.preventDefault();
        } else if (inTextField) {
          target?.blur();
          e.preventDefault();
        }
        return;
      }

      if (inTextField) return;

      // Defensive: every single-letter shortcut would also match its
      // Ctrl/Cmd/Alt-modified version under `e.key === 'a'`, swallowing
      // OS gestures the practitioner expects (⌘A select-all, ⌘R reload,
      // ⌘F find, etc.). Return early when any modifier is held — the OS
      // / browser gets the keystroke without engine interference.
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      switch (e.key) {
        case 'ArrowLeft':
        case 'k':
          goPrev();
          break;
        case 'ArrowRight':
        case 'j':
          goNext();
          break;
        case 'n':
          goNextPending();
          break;
        case 'p':
          goPrevPending();
          break;
        case '?':
          setShortcutsOpen((open) => !open);
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // currentIndex: goNextPending reads the cursor directly (it seeds the
    // cross-page pending walk), so the handler must rebind per cursor move.
  }, [queueItems, shortcutsOpen, currentIndex]);

  const current = queueItems[currentIndex] ?? null;
  // Two-stage lookup: prefer the in-memory ruleIndex (built from
  // /api/rules → latest-version-per-id). If the finding fired against
  // a non-latest version, the index misses; lazily fetch from the
  // /api/rules/{id}/v{version} point-lookup so the card can still
  // render the rule the engine actually evaluated. Without this, the
  // card shows "Rule X@vN isn't in the catalog" even though the YAML
  // is sitting on disk.
  const indexHit = current
    ? ruleIndex.get(`${current.rule_id}@v${current.rule_version}`) ?? null
    : null;
  const historicalRuleQuery = useQuery({
    queryKey: [
      'rule-version',
      current?.rule_id ?? '',
      current?.rule_version ?? 0,
      userKey,
    ],
    enabled: Boolean(current) && indexHit === null,
    queryFn: async () =>
      getRuleVersion(current!.rule_id, current!.rule_version, (await getIdToken()) ?? ''),
    staleTime: 5 * 60 * 1000,
  });
  const currentRule: RuleOut | null = indexHit ?? historicalRuleQuery.data ?? null;

  return (
    <div className="grid gap-5">
      <div className="flex items-start justify-between gap-3">
        <ReviewSummary
          reviewedPct={reviewedPct}
          reviewedCount={reviewedCount}
          totalCount={totalCount}
          attachmentReady={linksReady}
          attachedCount={attachedCount}
        />
        <button
          type="button"
          onClick={() => setShortcutsOpen(true)}
          className="shrink-0 rounded-md border border-border bg-card px-2 py-1 text-xs text-brand-slate transition-colors hover:border-brand-royal/40 hover:text-brand-royal"
          title="Keyboard shortcuts"
        >
          <kbd className="font-mono text-xs text-brand-navy">?</kbd>{' '}
          shortcuts
        </button>
      </div>

      {findings.some((f) => f.auto_carried_forward) && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-brand-slate/30 bg-muted/30 px-3 py-2 text-xs">
          <label className="flex items-center gap-2 text-brand-navy">
            <input
              type="checkbox"
              checked={carriedOnlyFilter}
              onChange={(ev) => setCarriedOnlyFilter(ev.target.checked)}
            />
            <span>
              Show only auto-carried findings (
              {findings.filter((f) => f.auto_carried_forward).length})
            </span>
          </label>
          <span className="text-brand-slate">
            Carried from a prior run — overrides clear the flag.
          </span>
        </div>
      )}

      <FindingFilterBar
        ruleFilter={ruleFilter}
        setRuleFilter={setRuleFilter}
        provinceFilter={provinceFilter}
        setProvinceFilter={setProvinceFilter}
        supplierFilter={supplierFilter}
        setSupplierFilter={setSupplierFilter}
        transactionTypeFilter={transactionTypeFilter}
        setTransactionTypeFilter={setTransactionTypeFilter}
        classFilter={classFilter}
        setClassFilter={setClassFilter}
        glAccountFilter={glAccountFilter}
        setGlAccountFilter={setGlAccountFilter}
        currencyFilter={currencyFilter}
        setCurrencyFilter={setCurrencyFilter}
        outcomeFilter={outcomeFilter}
        setOutcomeFilter={setOutcomeFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        approvalReasonFilter={approvalReasonFilter}
        setApprovalReasonFilter={setApprovalReasonFilter}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        amountMin={amountMin}
        setAmountMin={setAmountMin}
        amountMax={amountMax}
        setAmountMax={setAmountMax}
        effRateMin={effRateMin}
        setEffRateMin={setEffRateMin}
        effRateMax={effRateMax}
        setEffRateMax={setEffRateMax}
        reasonCodesFilter={reasonCodesFilter}
        setReasonCodesFilter={setReasonCodesFilter}
        invoiceFilter={invoiceFilter}
        setInvoiceFilter={setInvoiceFilter}
        invoiceLookupFailed={linksError}
        sourceFilter={sourceFilter}
        setSourceFilter={setSourceFilter}
        sources={sources}
        ledgerSourceFilter={ledgerSourceFilter}
        setLedgerSourceFilter={setLedgerSourceFilter}
        ledgerSources={ledgerSources}
        ruleIds={ruleIds}
        ruleById={ruleById}
        provinces={provinces}
        suppliers={suppliers}
        transactionTypes={transactionTypes}
        classes={classes}
        currencies={currencies}
        totalCount={totalCount}
        showingCount={queueTotal}
        hasActiveFilter={hasActiveFilter}
        onClearAll={clearAllFilters}
      />

      {canReview && filtered.length > 0 && (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setBulkNeedsReviewOpen(true)}
            disabled={bulkProgress !== null && bulkProgress.done < bulkProgress.total}
          >
            Flag {filtered.length} for review
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setBulkRejectOpen(true)}
            disabled={bulkProgress !== null && bulkProgress.done < bulkProgress.total}
          >
            Reject {filtered.length}
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => setBulkApproveOpen(true)}
            disabled={bulkProgress !== null && bulkProgress.done < bulkProgress.total}
          >
            Approve {filtered.length}
          </Button>
        </div>
      )}

      {bulkProgress !== null && (
        <div className="rounded-md border border-brand-royal/30 bg-brand-royal/5 px-3 py-2 text-sm">
          <p className="font-medium text-brand-navy">
            Bulk {bulkProgress.action}: {bulkProgress.done} of {bulkProgress.total}
            {bulkProgress.errors > 0 && (
              <span className="ml-2 text-destructive">({bulkProgress.errors} failed)</span>
            )}
          </p>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-brand-royal transition-all"
              style={{
                width: `${Math.round((bulkProgress.done / Math.max(1, bulkProgress.total)) * 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {current ? (
        <FindingCard
          key={current.finding_id}
          finding={current}
          rule={currentRule}
          // OR in the historical-version fetch's loading + error state so
          // the "rule isn't in the catalog" warning doesn't flash while
          // we look up a non-latest version.
          rulesLoading={rulesLoading || historicalRuleQuery.isFetching}
          rulesError={rulesError || historicalRuleQuery.isError}
          canReview={canReview}
          runId={runId}
          onAfterReview={goNext}
          pendingBySupplier={pendingBySupplier}
          onBulkForSupplier={startBulkForSupplier}
          onOpenSupplier={openSupplierPage}
          invoiceLinks={linksByTransaction.get(current.transaction_id)}
          isMarkedNoInvoice={markedTransactions.has(current.transaction_id)}
          invoiceExtractions={extractionsByDocument}
          claims={claimsByTransaction.get(current.transaction_id)}
          claimPeriodCount={claimPeriodCount}
          claimsReady={claimsReady}
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>No findings to review</CardTitle>
            <CardDescription>
              {hasActiveFilter
                ? 'No findings match the active filters. Clear them to see others.'
                : 'This run produced zero matches.'}
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {queueError && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
          <span className="text-destructive">
            Loading findings failed — the queue may only show part of the
            filtered set. Check your connection.
          </span>
          <Button type="button" size="sm" variant="outline" onClick={retryQueue}>
            Retry
          </Button>
        </div>
      )}

      {queueItems.length > 0 && (
        <QueueNav
          currentIndex={currentIndex}
          total={queueTotal}
          loadedCount={queueItems.length}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}

      <ShortcutsHelpDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />

      <Dialog
        open={bulkApproveOpen}
        onClose={() => {
          setBulkApproveOpen(false);
          setBulkApproveNote('');
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk approve {filtered.length} findings?</DialogTitle>
          <DialogDescription>
            Approves every finding inside the active filter — total
            recoverable{' '}
            <span className="font-mono">{formatCurrency(String(filteredTotal))}</span>
            . The same note is recorded on every finding; the audit trail
            keeps individual reviewer + timestamp per finding. Findings
            already approved stay approved; rejected findings flip to
            approved (re-review).
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (bulkApproveNote.trim().length === 0) return;
            void runBulkApprove();
          }}
        >
          <textarea
            value={bulkApproveNote}
            onChange={(e) => setBulkApproveNote(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Why are these approvable? (required, applies to all)"
            maxLength={256}
            required
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkApproveOpen(false);
                setBulkApproveNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={bulkApproveNote.trim().length === 0}
            >
              Approve all {filtered.length}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      <Dialog
        open={bulkNeedsReviewOpen}
        onClose={() => {
          setBulkNeedsReviewOpen(false);
          setBulkNeedsReviewNote('');
        }}
      >
        <DialogHeader>
          <DialogTitle>
            Flag {filtered.length} findings as needs review?
          </DialogTitle>
          <DialogDescription>
            Marks every finding inside the active filter as needs-review —
            total at-stake{' '}
            <span className="font-mono">{formatCurrency(String(filteredTotal))}</span>
            . The same note is recorded on every finding; the audit trail
            keeps individual reviewer + timestamp per finding. Use this
            when you can't approve or reject from the available evidence
            (waiting on the customer, second reviewer, etc.).
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (bulkNeedsReviewNote.trim().length === 0) return;
            void runBulkNeedsReview();
          }}
        >
          <textarea
            value={bulkNeedsReviewNote}
            onChange={(e) => setBulkNeedsReviewNote(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Why can't these be decided yet? (required, applies to all)"
            maxLength={256}
            required
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkNeedsReviewOpen(false);
                setBulkNeedsReviewNote('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={bulkNeedsReviewNote.trim().length === 0}
            >
              Flag all {filtered.length}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

      <Dialog
        open={bulkRejectOpen}
        onClose={() => {
          setBulkRejectOpen(false);
          setBulkRejectReason('');
          setBulkRejectCodes([]);
        }}
      >
        <DialogHeader>
          <DialogTitle>Bulk reject {filtered.length} findings?</DialogTitle>
          <DialogDescription>
            Rejects every finding inside the active filter — total at-risk{' '}
            <span className="font-mono">{formatCurrency(String(filteredTotal))}</span>
            . The same reason + codes are recorded on every finding; the
            audit trail keeps individual reviewer + timestamp per finding.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            // At least one signal required — codes OR text.
            if (bulkRejectReason.trim().length === 0 && bulkRejectCodes.length === 0) return;
            void runBulkReject();
          }}
        >
          <ReasonCodesPicker value={bulkRejectCodes} onChange={setBulkRejectCodes} />
          <textarea
            value={bulkRejectReason}
            onChange={(e) => setBulkRejectReason(e.target.value)}
            onKeyDown={submitFormOnCmdEnter}
            placeholder="Optional when codes are picked — otherwise required (applies to all)"
            maxLength={256}
            className="min-h-24 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <DialogFooter>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                setBulkRejectOpen(false);
                setBulkRejectReason('');
                setBulkRejectCodes([]);
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              variant="destructive"
              disabled={bulkRejectReason.trim().length === 0 && bulkRejectCodes.length === 0}
            >
              Reject all {filtered.length}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>

    </div>
  );
}

function ReviewSummary({
  reviewedPct,
  reviewedCount,
  totalCount,
  attachmentReady,
  attachedCount,
}: {
  reviewedPct: number;
  reviewedCount: number;
  totalCount: number;
  attachmentReady: boolean;
  attachedCount: number;
}) {
  // The per-status count/dollar chips moved to the persistent run header;
  // this block keeps the queue-specific progress bar + invoice-attachment
  // breakdown behind a collapsible so the header stays uncluttered.
  const [detailsOpen, setDetailsOpen] = useState(false);
  return (
    <div className="grid flex-1 gap-3">
      <div>
        <button
          type="button"
          onClick={() => setDetailsOpen((o) => !o)}
          aria-expanded={detailsOpen}
          className="flex items-center gap-1.5 text-xs text-brand-slate transition-colors hover:text-brand-royal"
        >
          <ChevronDown
            aria-hidden="true"
            className={cn('h-3.5 w-3.5 transition-transform', detailsOpen && 'rotate-180')}
          />
          Details
          <span className="text-brand-slate/70">· {reviewedPct}% reviewed</span>
        </button>

        {detailsOpen && (
          <div className="mt-2 grid gap-2.5">
            <div>
              <div className="flex items-baseline justify-between text-xs">
                <span className="text-brand-slate">
                  {reviewedCount} of {totalCount} reviewed
                </span>
                <span className="font-mono text-brand-navy">{reviewedPct}%</span>
              </div>
              <div
                className="mt-1 h-2 overflow-hidden rounded-full bg-muted"
                role="progressbar"
                aria-valuenow={reviewedPct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full bg-brand-royal transition-all"
                  style={{ width: `${reviewedPct}%` }}
                />
              </div>
            </div>

            {attachmentReady && (
              <p className="text-xs text-brand-slate">
                📎 {attachedCount} of {totalCount} findings have an invoice attached
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QueueNav({
  currentIndex,
  total,
  loadedCount,
  onPrev,
  onNext,
}: {
  currentIndex: number;
  /** Filter-matched queue size (server total in paged mode). */
  total: number;
  /** Cards loaded so far — the advance bound in paged mode. */
  loadedCount: number;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onPrev}
        disabled={currentIndex <= 0}
      >
        ← Prev
      </Button>
      <span className="text-brand-slate">
        {currentIndex + 1} of {total}
        <span className="ml-2 text-xs text-brand-slate/60">(← / → to navigate)</span>
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={onNext}
        disabled={currentIndex >= Math.min(loadedCount, total) - 1}
      >
        Next →
      </Button>
    </div>
  );
}

/** Modal listing every keyboard shortcut the review queue supports.
 *  Opens on `?`, closes on Escape or the close button. Lives next to
 *  the bulk-action dialogs since both are page-level overlays. */
function ShortcutsHelpDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const groups: Array<{ heading: string; rows: Array<[string, string]> }> = [
    {
      heading: 'Navigation',
      rows: [
        ['← / k', 'Previous finding'],
        ['→ / j', 'Next finding'],
        ['p', 'Previous pending finding (skip already reviewed)'],
        ['n', 'Next pending finding'],
      ],
    },
    {
      heading: 'Disposition',
      rows: [
        ['a', 'Approve current finding (no note)'],
        ['r', 'Focus reject-reason textarea (type, then ⌘/Ctrl+Enter to submit)'],
        ['f', 'Focus needs-review textarea — flag the finding for follow-up (requires a note)'],
        ['⌘/Ctrl + Enter', 'Submit from inside the textarea — approves if empty, rejects if a reason is typed'],
      ],
    },
    {
      heading: 'Context',
      rows: [
        ['e', 'Expand or collapse the "Full evidence" details block'],
      ],
    },
    {
      heading: 'General',
      rows: [
        ['?', 'Open / close this shortcuts help'],
        ['Escape', 'Close this dialog · or unfocus a textarea so navigation keys work again'],
      ],
    },
  ];
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Keyboard shortcuts</DialogTitle>
      </DialogHeader>
      <div className="grid gap-5 px-1 pb-2">
        {groups.map((g) => (
          <section key={g.heading} className="grid gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-brand-slate">
              {g.heading}
            </h4>
            <dl className="grid grid-cols-[max-content_minmax(0,1fr)] gap-x-6 gap-y-1.5 text-sm">
              {g.rows.map(([key, label]) => (
                <div key={key} className="contents">
                  <dt>
                    <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs text-brand-navy">
                      {key}
                    </kbd>
                  </dt>
                  <dd className="self-baseline text-brand-slate">{label}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
      <DialogFooter>
        <Button type="button" size="sm" variant="outline" onClick={onClose}>
          Close
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
